import { ArrowDropDown, ArrowDropUp, Close, FilterList } from '@mui/icons-material';
import { Box, Button, Grid, MenuItem } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { IDropdownResponse } from '../../../models';
import useCollapse from 'react-collapsed';
import { getJobStatuses, getJobTypes } from '../../../fetch';
import { NeighborhoodDropdown, SMBSelect } from '../../../components';

interface IFieldWalkthroughFilterProps {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean, clearSearch?: boolean) => void;
  hasAppliedFilters?: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  selectedNeighborhood: string;
  setSelectedNeighborhood: (val: string) => void;
  selectedStatus: string;
  setSelectedStatus: (val: string) => void;
  selectedType: string;
  setSelectedType: (val: string) => void;
}

export const SiteReportsFilters: FC<IFieldWalkthroughFilterProps> = ({
  isLoading,
  applyFilters,
  hasAppliedFilters,
  setHasAppliedFilters,
  selectedNeighborhood,
  setSelectedNeighborhood,
  selectedStatus,
  setSelectedStatus,
  selectedType,
  setSelectedType
}) => {
  const classes = useStyles();

  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true
  });

  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [statuses, setStatuses] = useState<IDropdownResponse[]>([]);
  const fetchStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      const res = await getJobStatuses();
      setStatuses(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [types, setTypes] = useState<IDropdownResponse[]>([]);
  const fetchTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const res = await getJobTypes();
      setTypes(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleDisableButton = () => {
    if (isLoading || isLoadingStatuses || isLoadingTypes || !selectedNeighborhood) return true;
    return false;
  };

  useEffect(() => {
    fetchStatuses();
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        display="flex"
        width={{
          xs: '100%',
          sm: 'auto'
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          className={classes.mobileButton}
          startIcon={<FilterList />}
          endIcon={isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
          disabled={isLoading}
          {...getToggleProps()}
        >
          Filters
        </Button>
      </Box>
      <Box {...getCollapseProps()}>
        <Grid container alignItems="center" spacing={1} marginTop={theme => theme.spacing(0.5)}>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <NeighborhoodDropdown
              value={selectedNeighborhood}
              handleChange={value => {
                if (typeof value === 'string' && value.length === 0) {
                  if (hasAppliedFilters && !selectedStatus && !selectedType) {
                    setHasAppliedFilters(false);
                  }
                  return setSelectedNeighborhood('');
                }
                setSelectedNeighborhood(value.toString());
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              label={isLoadingStatuses ? 'Loading...' : 'Status'}
              disabled={isLoading || isLoadingStatuses}
              value={selectedStatus ? selectedStatus : ''}
              onChange={e => {
                setSelectedStatus(e.target.value);
              }}
              handleClear={() => setSelectedStatus('')}
            >
              {statuses &&
                !isLoadingStatuses &&
                statuses.map((status, index) => {
                  return (
                    <MenuItem key={`${status.value}-${index}`} value={status.value}>
                      {status.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              label={isLoadingTypes ? 'Loading...' : 'Type'}
              disabled={isLoading || isLoadingTypes}
              value={selectedType ? selectedType : ''}
              onChange={e => {
                setSelectedType(e.target.value);
              }}
              handleClear={() => setSelectedType('')}
            >
              {types &&
                !isLoadingTypes &&
                types.map((type, index) => {
                  return (
                    <MenuItem key={`${type.value}-${index}`} value={type.value}>
                      {type.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              disabled={handleDisableButton()}
              startIcon={<FilterList />}
              onClick={() => {
                setHasAppliedFilters(true);
                applyFilters();
              }}
            >
              Apply Filters
            </Button>
            {hasAppliedFilters && (
              <Button
                color="inherit"
                className={clsx(classes.button, classes.resetButton)}
                variant="contained"
                disabled={isLoading}
                startIcon={<Close />}
                onClick={() => {
                  setHasAppliedFilters(false);
                  setSelectedNeighborhood('');
                  setSelectedStatus('');
                  setSelectedType('');
                  applyFilters(true);
                }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  }
}));
